import { request } from './client'
import type { UploadTicket } from './types'

export interface UploadProgress {
  loaded: number
  total: number
}

interface UploadResult {
  uploadId: string
}

/** PUT the file to the presigned URL with XMLHttpRequest (fetch has no upload progress). */
function putWithProgress(
  ticket: UploadTicket,
  file: File,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(ticket.method, ticket.upload_url)
    for (const [key, value] of Object.entries(ticket.headers ?? {})) xhr.setRequestHeader(key, value)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress({ loaded: event.loaded, total: event.total })
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress({ loaded: file.size, total: file.size })
        resolve()
      } else reject(new Error(`upload_failed_${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('upload_network_error'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(file)
  })
}

/**
 * The upload flow (spec §7): create the upload, PUT the bytes with progress,
 * confirm it, then create the transcription.
 */
export async function uploadFile(
  file: File,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  const contentType = file.type || 'application/octet-stream'
  const ticket = await request<UploadTicket>('/v1/uploads', {
    method: 'POST',
    body: { filename: file.name, content_type: contentType, size_bytes: file.size },
    signal,
  })
  await putWithProgress(ticket, file, onProgress, signal)
  await request(`/v1/uploads/${ticket.id}/complete`, { method: 'POST', signal })
  return { uploadId: ticket.id }
}
